import { useRef, useEffect, useLayoutEffect, useMemo, useState, KeyboardEvent } from 'react';
import { Message, WorldSeed } from '../types';
import { useActiveWorldStore, useAuthStore } from '../store';
import { useSidebarStore } from '../store/ui';
import { useIsMobile } from '../hooks';
import { getAllMessages } from '../api';
import { MessageActions } from './MessageActions';
import { InspectModal } from './InspectModal';
import { WorldSettingsModal } from './WorldSettingsModal';
import { StatusBar } from './StatusBar';
import { ErrorNotice } from './ErrorNotice';

// Font size options
export type FontSize = 'small' | 'medium' | 'large' | 'xlarge';
const FONT_SIZES: Record<FontSize, string> = {
  small: '13px',
  medium: '15px',
  large: '17px',
  xlarge: '19px',
};

// Get font size from localStorage or default
function getStoredFontSize(worldId: string): FontSize {
  try {
    const stored = localStorage.getItem(`textborne:fontSize:${worldId}`);
    if (stored && stored in FONT_SIZES) return stored as FontSize;
  } catch {}
  return 'medium';
}

// Save font size to localStorage
function setStoredFontSize(worldId: string, size: FontSize) {
  try {
    localStorage.setItem(`textborne:fontSize:${worldId}`, size);
  } catch {}
}

// Simulation status phase colors (for buffered mode)
const SIMULATION_PHASE_COLORS: Record<string, string> = {
  context: 'text-blue-400',
  generation: 'text-yellow-400',
  guardrails: 'text-purple-400',
  complete: 'text-green-400',
};

// Editable message bubble component
function EditableMessageContent({
  message,
  isEditing,
  editContent,
  onSave,
  onCancel,
  fontSize,
}: {
  message: Message;
  isEditing: boolean;
  editContent: string;
  onSave: (newContent: string) => void;
  onCancel: () => void;
  fontSize: string;
}) {
  const editableRef = useRef<HTMLDivElement>(null);

  // Auto-focus when entering edit mode
  useEffect(() => {
    if (isEditing && editableRef.current) {
      editableRef.current.focus();
      // Move cursor to end
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(editableRef.current);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [isEditing]);

  const getCurrentContent = () => {
    return editableRef.current?.innerText || editContent;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSave(getCurrentContent());
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  const handleBlur = () => {
    onSave(getCurrentContent());
  };

  if (isEditing) {
    return (
      <div
        ref={editableRef}
        contentEditable
        suppressContentEditableWarning
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={`outline-none whitespace-pre-wrap ${
          message.role === 'user'
            ? 'text-dark-50'
            : 'text-dark-50 narrative-text'
        }`}
        style={{ fontSize }}
      >
        {editContent}
      </div>
    );
  }

  if (message.role === 'user') {
    return (
      <div className="flex items-start gap-2">
        <span className="text-primary-200 font-medium" style={{ fontSize }}>&gt;</span>
        <p className="whitespace-pre-wrap" style={{ fontSize }}>{message.content}</p>
      </div>
    );
  }

  return (
    <p className="narrative-text whitespace-pre-wrap" style={{ fontSize }}>{message.content}</p>
  );
}

interface NarrativeViewProps {
  messages: Message[];
  isStreaming: boolean;
  streamingContent: string;
  onSendMessage: (content: string) => void;
  onEditMessage: (messageId: string, newContent: string) => void;
  onDeleteMessage: (messageId: string) => void;
  onRegenerateMessage: (messageId: string) => void;
  /** Start a new world from the story at a message; absent where stories cannot branch. */
  onBranchMessage?: (messageId: string) => Promise<void>;
  onStopGeneration: () => void;
  worldTitle: string;
  worldId: string;
  worldSeed: WorldSeed | null;
  onSettingsUpdated: (updatedSeed: Partial<WorldSeed>, updatedTitle?: string) => void;
  isCompanion?: boolean;
  onOpenCompanionMemories?: () => void;
  onOpenCompanionSettings?: () => void;
  /** Fire to request a batch of messages older than the oldest currently held.
   *  The handler is responsible for gating on hasMoreHistory + not-loading. */
  onLoadOlder?: () => void;
}

const INPUT_SUGGESTIONS: readonly string[] = [
  'look around',
  'introduce yourself',
  'examine what\'s in front of you',
  'listen for anything unusual',
  'ask them what happened',
  'check your pockets',
  'take a breath and think',
];

export function NarrativeView({
  messages,
  isStreaming,
  streamingContent,
  onSendMessage,
  onEditMessage,
  onDeleteMessage,
  onRegenerateMessage,
  onBranchMessage,
  onStopGeneration,
  worldTitle,
  worldId,
  worldSeed,
  onSettingsUpdated,
  isCompanion,
  onOpenCompanionMemories,
  onOpenCompanionSettings,
  onLoadOlder,
}: NarrativeViewProps) {
  const [input, setInput] = useState('');
  const [suggestionIdx, setSuggestionIdx] = useState(0);
  const [showWorldSettings, setShowWorldSettings] = useState(false);
  const [inspectLogId, setInspectLogId] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [fontSize, setFontSize] = useState<FontSize>(() => getStoredFontSize(worldId));
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  // Track the current draft input before history navigation started
  const [draftInput, setDraftInput] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const overflowMenuRef = useRef<HTMLDivElement>(null);
  // Scroll-back pagination refs: container is the scrollable viewport, the
  // sentinel sits above the first message, the anchor captures pre-prepend
  // geometry so we can restore the visual position after older messages land.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const pendingAnchorRef = useRef<{ oldScrollHeight: number; oldScrollTop: number } | null>(null);
  const prevLastMsgIdRef = useRef<string | null>(null);
  const isConnected = useActiveWorldStore(state => state.isConnected);
  const simulationStatus = useActiveWorldStore(state => state.simulationStatus);
  const inputHistory = useActiveWorldStore(state => state.inputHistory);
  const inputHistoryIndex = useActiveWorldStore(state => state.inputHistoryIndex);
  const pushInputHistory = useActiveWorldStore(state => state.pushInputHistory);
  const setInputHistoryIndex = useActiveWorldStore(state => state.setInputHistoryIndex);
  const clearInputHistoryNavigation = useActiveWorldStore(state => state.clearInputHistoryNavigation);
  const restoredInput = useActiveWorldStore(state => state.restoredInput);
  const clearRestoredInput = useActiveWorldStore(state => state.clearRestoredInput);
  const ephemeralNotices = useActiveWorldStore(state => state.ephemeralNotices);
  const isLoadingOlderHistory = useActiveWorldStore(state => state.isLoadingOlderHistory);
  const hasMoreHistory = useActiveWorldStore(state => state.hasMoreHistory);

  // Rotate placeholder suggestions while the input is empty so new users see
  // example actions rather than a single cryptic prompt. Pauses the instant
  // they start typing; resumes when the input is cleared.
  const isInputEmpty = input.length === 0;
  useEffect(() => {
    if (!isInputEmpty) return;
    const id = setInterval(() => {
      setSuggestionIdx(i => (i + 1) % INPUT_SUGGESTIONS.length);
    }, 3500);
    return () => clearInterval(id);
  }, [isInputEmpty]);

  // Merge persisted messages with ephemeral notices (errors, info) into a
  // single chronologically-ordered timeline. Notices are tagged 'notice' so
  // the renderer can dispatch to the ErrorNotice component; messages render
  // as before.
  type TimelineEntry =
    | { kind: 'message'; id: string; created_at: string; message: Message }
    | { kind: 'notice'; id: string; created_at: string; notice: typeof ephemeralNotices[number] };

  const timelineEntries = useMemo<TimelineEntry[]>(() => {
    const msgEntries: TimelineEntry[] = messages.map((m) => ({
      kind: 'message',
      id: m.id,
      created_at: m.created_at,
      message: m,
    }));
    const noticeEntries: TimelineEntry[] = ephemeralNotices.map((n) => ({
      kind: 'notice',
      id: n.id,
      created_at: n.created_at,
      notice: n,
    }));
    return [...msgEntries, ...noticeEntries].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  }, [messages, ephemeralNotices]);
  const user = useAuthStore(state => state.user);
  const isAdmin = !!user?.is_admin;

  const isMobile = useIsMobile();
  const toggleSidebar = useSidebarStore(state => state.toggleSidebar);

  // Computed font size in pixels
  const fontSizePx = FONT_SIZES[fontSize];

  // Handle font size change
  const handleFontSizeChange = (newSize: FontSize) => {
    setFontSize(newSize);
    setStoredFontSize(worldId, newSize);
  };

  // Start editing a message
  const handleStartEdit = (messageId: string, content: string) => {
    setEditingMessageId(messageId);
    setEditContent(content);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditContent('');
  };

  // Save edited message
  const handleSaveEdit = (messageId: string, newContent: string) => {
    const originalContent = messages.find(m => m.id === messageId)?.content;
    if (newContent.trim() && newContent.trim() !== originalContent) {
      onEditMessage(messageId, newContent.trim());
    }
    setEditingMessageId(null);
    setEditContent('');
  };

  // Auto-scroll to bottom when a NEW message arrives (last-message id changed)
  // or while streaming. Prepends of older history don't change the last-id, so
  // this effect doesn't fire for them — preventing the jarring jump-to-bottom
  // when the scroll-back sentinel triggers a load.
  useLayoutEffect(() => {
    const lastId = messages[messages.length - 1]?.id ?? null;
    const prev = prevLastMsgIdRef.current;
    prevLastMsgIdRef.current = lastId;
    if (lastId !== prev || streamingContent) {
      // scrollIntoView also moves overflow-hidden ancestors, which can pan
      // the entire chat behind the rail. Keep scrolling inside this pane;
      // streaming updates must not queue competing smooth animations.
      const container = scrollContainerRef.current;
      if (container) container.scrollTop = container.scrollHeight;
    }
  }, [messages, streamingContent, ephemeralNotices]);

  // Scroll-back: fire onLoadOlder when the top sentinel enters the viewport.
  // Capture pre-prepend geometry synchronously in the observer callback so
  // the anchor-restore effect has something to work with.
  useEffect(() => {
    if (!onLoadOlder) return;
    const sentinel = topSentinelRef.current;
    const container = scrollContainerRef.current;
    if (!sentinel || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (!hasMoreHistory || isLoadingOlderHistory) return;
        // Capture BEFORE triggering the WS send so the post-prepend restore
        // has an accurate baseline (the load is async; React will re-render
        // with the new messages, and useLayoutEffect below will compensate).
        pendingAnchorRef.current = {
          oldScrollHeight: container.scrollHeight,
          oldScrollTop: container.scrollTop,
        };
        onLoadOlder();
      },
      { root: container, rootMargin: '200px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadOlder, hasMoreHistory, isLoadingOlderHistory]);

  // After a prepend, restore the visual scroll position so the user's
  // currently-reading message stays put while new older ones appear above.
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    const anchor = pendingAnchorRef.current;
    if (!container || !anchor) return;
    // Only apply if the content actually grew (prepend completed).
    if (container.scrollHeight > anchor.oldScrollHeight) {
      container.scrollTop = container.scrollHeight - anchor.oldScrollHeight + anchor.oldScrollTop;
      pendingAnchorRef.current = null;
    }
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [input]);

  // Restore input after rate limit error
  useEffect(() => {
    if (restoredInput) {
      setInput(restoredInput);
      clearRestoredInput();
      // Focus the textarea
      textareaRef.current?.focus();
    }
  }, [restoredInput, clearRestoredInput]);

  // Close overflow menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (overflowMenuRef.current && !overflowMenuRef.current.contains(e.target as Node)) {
        setShowOverflowMenu(false);
      }
    };
    if (showOverflowMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOverflowMenu]);

  const handleSubmit = () => {
    if (!input.trim() || isStreaming || !isConnected) return;
    // Push to input history before sending
    console.log('[history] Pushing to history:', input.trim());
    pushInputHistory(input.trim());
    onSendMessage(input.trim());
    setInput('');
    // Clear history navigation state
    setDraftInput(null);
    clearInputHistoryNavigation();
    // Refocus the textarea so user can keep typing
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
      return;
    }

    // Escape: clear history navigation and return to empty/draft
    if (e.key === 'Escape') {
      if (inputHistoryIndex !== -1) {
        e.preventDefault();
        // Return to draft or empty
        setInput(draftInput ?? '');
        setDraftInput(null);
        clearInputHistoryNavigation();
      }
      return;
    }

    // Arrow key history navigation
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      const textarea = textareaRef.current;
      console.log('[history] Arrow key pressed:', e.key, 'history length:', inputHistory.length, 'index:', inputHistoryIndex);
      if (!textarea || inputHistory.length === 0) return;

      const { selectionStart, selectionEnd, value } = textarea;
      const isAtTop = selectionStart === 0 && selectionEnd === 0;
      const isAtBottom = selectionStart === value.length && selectionEnd === value.length;

      // For multiline: check if cursor is on first/last line
      const textBeforeCursor = value.substring(0, selectionStart);
      const textAfterCursor = value.substring(selectionEnd);
      const isOnFirstLine = !textBeforeCursor.includes('\n');
      const isOnLastLine = !textAfterCursor.includes('\n');

      if (e.key === 'ArrowUp' && (isAtTop || isOnFirstLine)) {
        e.preventDefault();

        // Save draft if starting navigation
        if (inputHistoryIndex === -1) {
          setDraftInput(input);
        }

        // Navigate to older entry
        const newIndex = inputHistoryIndex === -1
          ? inputHistory.length - 1
          : Math.max(0, inputHistoryIndex - 1);

        setInputHistoryIndex(newIndex);
        setInput(inputHistory[newIndex]);

        // Preserve cursor at start (top)
        setTimeout(() => {
          textarea.setSelectionRange(0, 0);
        }, 0);
      } else if (e.key === 'ArrowDown' && (isAtBottom || isOnLastLine)) {
        e.preventDefault();

        if (inputHistoryIndex === -1) return; // Not navigating history

        // Navigate to newer entry or back to draft
        const newIndex = inputHistoryIndex + 1;

        if (newIndex >= inputHistory.length) {
          // Return to draft
          setInput(draftInput ?? '');
          setDraftInput(null);
          clearInputHistoryNavigation();
          // Cursor at end
          setTimeout(() => {
            const len = textarea.value.length;
            textarea.setSelectionRange(len, len);
          }, 0);
        } else {
          setInputHistoryIndex(newIndex);
          setInput(inputHistory[newIndex]);
          // Cursor at end of new content
          setTimeout(() => {
            const len = inputHistory[newIndex].length;
            textarea.setSelectionRange(len, len);
          }, 0);
        }
      }
    }
  };

  // Handle input change - clear history navigation if user types
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setInput(newValue);

    // If navigating history and user modifies text, exit navigation
    if (inputHistoryIndex !== -1) {
      const currentHistoryValue = inputHistory[inputHistoryIndex];
      if (newValue !== currentHistoryValue) {
        setDraftInput(null);
        clearInputHistoryNavigation();
      }
    }
  };

  const formatTimestamp = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const [isCopying, setIsCopying] = useState(false);

  const copyToClipboard = async () => {
    if (isCopying) return;
    setIsCopying(true);

    try {
      // Fetch ALL messages from the API, not just the rendered ones
      const allMessages = await getAllMessages(worldId);

      const chatText = allMessages
        .map(msg => {
          const role = msg.role === 'user' ? 'User:' : 'Simulation:';
          return `${role}\n${msg.content}\n`;
        })
        .join('\n');

      await navigator.clipboard.writeText(chatText);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    } finally {
      setIsCopying(false);
    }
  };

  // Build overflow menu items for mobile
  const overflowItems: Array<{ label: string; onClick: () => void }> = [];
  overflowItems.push({
    label: copyFeedback ? 'Copied!' : isCopying ? 'Loading...' : 'Copy Chat',
    onClick: () => { copyToClipboard(); setShowOverflowMenu(false); },
  });
  if (!isCompanion) {
    overflowItems.push({
      label: 'Settings',
      onClick: () => { setShowWorldSettings(true); setShowOverflowMenu(false); },
    });
  }
  if (isCompanion) {
    overflowItems.push({
      label: 'Memories',
      onClick: () => { onOpenCompanionMemories?.(); setShowOverflowMenu(false); },
    });
    overflowItems.push({
      label: 'Boundaries',
      onClick: () => { onOpenCompanionSettings?.(); setShowOverflowMenu(false); },
    });
  }

  return (
    <div className="flex h-full min-w-0 overflow-hidden">
      {/* Main narrative area */}
      <div className="flex-1 flex flex-col min-h-0 min-w-0">
        {/* Header */}
        <header className="flex shrink-0 items-center justify-between px-3 py-3 md:px-6 md:py-4 border-b border-dark-500 bg-dark-700">
          <div className="flex flex-1 items-center gap-2 md:gap-3 min-w-0">
            {/* Mobile hamburger */}
            {isMobile && (
              <button
                onClick={toggleSidebar}
                className="text-dark-200 hover:text-dark-50 p-1 shrink-0"
                aria-label="Open menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}
            <h1 className="text-lg md:text-xl font-semibold text-dark-50 truncate">{worldTitle}</h1>
            {/* Genre/Tone Badges - hidden on mobile */}
            {!isMobile && worldSeed?.genre && (
              <span className="text-xs px-2 py-0.5 bg-purple-900/40 text-purple-300 rounded-full border border-purple-700/50">
                {worldSeed.genre}
              </span>
            )}
            {!isMobile && worldSeed?.tone && (
              <span className="text-xs px-2 py-0.5 bg-indigo-900/30 text-indigo-300 rounded-full border border-indigo-700/40">
                {worldSeed.tone}
              </span>
            )}
            <span className={`w-2 h-2 rounded-full shrink-0 ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
          </div>

          {/* Desktop action buttons */}
          {!isMobile && (
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={copyToClipboard}
                className="btn-secondary text-sm relative"
                disabled={messages.length === 0 || isCopying}
              >
                {copyFeedback ? (
                  <span className="text-green-400">Copied!</span>
                ) : isCopying ? (
                  <span className="text-dark-300">Loading...</span>
                ) : (
                  <>Copy Chat</>
                )}
              </button>
              {isCompanion && (
                <>
                  <button
                    onClick={onOpenCompanionMemories}
                    className="btn-secondary text-sm"
                  >
                    Memories
                  </button>
                  <button
                    onClick={onOpenCompanionSettings}
                    className="btn-secondary text-sm"
                  >
                    Boundaries
                  </button>
                </>
              )}
              {!isCompanion && (
                <button
                  onClick={() => setShowWorldSettings(true)}
                  className="btn-secondary text-sm p-2"
                  title="World Settings"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Mobile overflow menu */}
          {isMobile && (
            <div className="relative" ref={overflowMenuRef}>
              <button
                onClick={() => setShowOverflowMenu(!showOverflowMenu)}
                className="text-dark-200 hover:text-dark-50 p-2"
                aria-label="More actions"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>
              {showOverflowMenu && (
                <div className="absolute right-0 top-full mt-1 w-52 bg-dark-700 border border-dark-500 rounded-lg shadow-lg z-20 py-1">
                  {overflowItems.map((item, i) => (
                    <button
                      key={i}
                      onClick={item.onClick}
                      className="w-full px-4 py-3 text-left text-sm text-dark-200 hover:bg-dark-600 hover:text-dark-50"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </header>

        {/* Messages */}
        <div ref={scrollContainerRef} role="region" aria-label="Story messages" className="flex-1 min-h-0 min-w-0 overflow-y-auto px-3 py-4 md:px-6 space-y-4">
          {/* Top sentinel + status: IntersectionObserver watches this div. */}
          <div ref={topSentinelRef} aria-hidden="true" />
          {messages.length > 0 && hasMoreHistory && (
            <div className="flex items-center justify-center py-2 text-xs text-dark-400">
              {isLoadingOlderHistory ? (
                <span className="flex items-center gap-2">
                  <span className="spinner w-3 h-3" />
                  Loading earlier story…
                </span>
              ) : (
                <span className="opacity-60">Scroll up for earlier story</span>
              )}
            </div>
          )}
          {messages.length > 0 && !hasMoreHistory && (
            <div className="flex items-center gap-3 py-2 text-xs text-dark-500">
              <span className="flex-1 h-px bg-dark-600" />
              <span className="uppercase tracking-widest">Beginning of story</span>
              <span className="flex-1 h-px bg-dark-600" />
            </div>
          )}

          {messages.length === 0 && !streamingContent && (
            <div className="text-center text-dark-300 py-8">
              <p className="text-lg mb-2">Your story begins here...</p>
              <p className="text-sm">Type your first action or dialogue to start the narrative.</p>
            </div>
          )}

          {timelineEntries.map((entry) => {
            // Ephemeral notices (errors, budget warnings) render as left-aligned
            // callouts, same font as chat, with a Details disclosure for devs.
            if (entry.kind === 'notice') {
              return (
                <ErrorNotice
                  key={entry.id}
                  notice={entry.notice}
                  fontSize={fontSizePx}
                />
              );
            }
            const message = entry.message;
            return (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} group`}
              >
                <div
                  className={`min-w-0 [overflow-wrap:anywhere] max-w-[95%] md:max-w-[80%] rounded-lg px-4 py-3 text-sm ${
                    message.role === 'user'
                      ? 'bg-primary-600 text-white'
                      : 'bg-dark-600 text-dark-50'
                  } ${editingMessageId === message.id ? 'ring-2 ring-primary-400' : ''}`}
                >
                  <EditableMessageContent
                    message={message}
                    isEditing={editingMessageId === message.id}
                    editContent={editContent}
                    onSave={(newContent) => handleSaveEdit(message.id, newContent)}
                    onCancel={handleCancelEdit}
                    fontSize={fontSizePx}
                  />

                  {/* Timestamp and Message Actions on same line */}
                  <div className="flex items-center justify-between mt-2">
                    <div className={`text-xs ${
                      message.role === 'user' ? 'text-primary-200' : 'text-dark-300'
                    }`}>
                      {formatTimestamp(message.created_at)}
                    </div>

                    <MessageActions
                      message={message}
                      isEditing={editingMessageId === message.id}
                      onStartEdit={() => handleStartEdit(message.id, message.content)}
                      onCancelEdit={handleCancelEdit}
                      onEdit={onEditMessage}
                      onDelete={onDeleteMessage}
                      onRegenerate={onRegenerateMessage}
                      onBranch={onBranchMessage}
                      onInspect={(id) => {
                        const msg = messages.find(m => m.id === id);
                        if (msg?.inference_log_id) {
                          setInspectLogId(msg.inference_log_id);
                        }
                      }}
                      isStreaming={isStreaming}
                      canRegenerate={
                        (messages[messages.length - 1]?.role === 'user' && message.id === messages[messages.length - 1]?.id) ||
                        (messages[messages.length - 1]?.role === 'assistant' && messages[messages.length - 2]?.role === 'user' &&
                          (message.id === messages[messages.length - 1]?.id || message.id === messages[messages.length - 2]?.id))
                      }
                    />
                  </div>
                </div>
              </div>
            );
          })}

          {/* Streaming content */}
          {streamingContent && (
            <div className="flex justify-start">
              <div className="min-w-0 [overflow-wrap:anywhere] max-w-[95%] md:max-w-[80%] bg-dark-600 rounded-lg px-4 py-3">
                <p className="narrative-text whitespace-pre-wrap streaming-cursor" style={{ fontSize: fontSizePx }}>
                  {streamingContent}
                </p>
              </div>
            </div>
          )}

          {/* Typing indicator when waiting for response */}
          {isStreaming && !streamingContent && (
            <div className="flex justify-start">
              <div className="bg-dark-600 rounded-lg px-4 py-3">
                {simulationStatus ? (
                  // Buffered mode: show simulation engine status
                  <div className="flex items-center gap-3">
                    <span className={`text-2xl ${SIMULATION_PHASE_COLORS[simulationStatus.phase] || 'text-dark-300'}`}>
                      {simulationStatus.icon}
                    </span>
                    <div className="flex flex-col">
                      <span className="text-dark-100 text-sm font-medium">
                        {simulationStatus.status}
                      </span>
                      {simulationStatus.classification && (
                        <span className="text-dark-400 text-xs">
                          Response type: {simulationStatus.classification}
                        </span>
                      )}
                    </div>
                    <span className="w-4 h-4 border-2 border-primary-400 border-t-transparent rounded-full animate-spin ml-2" />
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-dark-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-dark-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-dark-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Status bar */}
        <StatusBar />

        {/* Input area */}
        <div className="shrink-0 px-3 py-3 md:px-6 md:py-4 border-t border-dark-500 bg-dark-700">
          <div className="flex gap-3">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={isConnected ? `e.g. ${INPUT_SUGGESTIONS[suggestionIdx]}` : "Connecting..."}
              disabled={!isConnected || isStreaming}
              className="narrative-input flex-1 min-w-0 px-4 py-3 resize-none min-h-[48px] max-h-[200px]"
              rows={1}
            />
            {isStreaming ? (
              <button
                onClick={onStopGeneration}
                className="btn-danger px-6 self-end"
                title="Stop generation"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="6" width="12" height="12" rx="1" />
                </svg>
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!input.trim() || !isConnected}
                className="btn-primary px-6 self-end"
              >
                Send
              </button>
            )}
          </div>
          <p className="text-dark-400 text-xs mt-2">
            {isMobile ? 'Enter to send, Shift+Enter for new line' : 'Enter to send, Shift+Enter for new line, \u2191/\u2193 for history'}
          </p>
        </div>
      </div>

      {/* Inspect Modal (Admin Only) */}
      {inspectLogId && (
        <InspectModal
          inferenceLogId={inspectLogId}
          onClose={() => setInspectLogId(null)}
        />
      )}

      {/* World Settings Modal (Narrative only) */}
      {!isCompanion && worldSeed && (
        <WorldSettingsModal
          isOpen={showWorldSettings}
          onClose={() => setShowWorldSettings(false)}
          worldId={worldId}
          worldTitle={worldTitle}
          worldSeed={worldSeed}
          onSettingsUpdated={onSettingsUpdated}
          fontSize={fontSize}
          onFontSizeChange={handleFontSizeChange}
        />
      )}
    </div>
  );
}
